<div class="mfa-step">
  <!-- 1. New recovery codes: saved before anything else -->
  <ng-container *ngIf="showRecoveryCodes; else codeStep">
    <h2 class="mfa-title">
      {{ 'LOGIN.MFA.RECOVERY-CODES-TITLE' | translate }}
    </h2>
    <p class="mfa-text">{{ 'LOGIN.MFA.RECOVERY-CODES-TEXT' | translate }}</p>
    <ol class="mfa-recovery-codes" id="mfa-recovery-codes">
      <li *ngFor="let code of formattedRecoveryCodes">
        <code>{{ code }}</code>
      </li>
    </ol>
    <div class="mfa-buttons">
      <button
        type="button"
        class="btn mfa-secondary-button"
        (click)="downloadRecoveryCodes()"
      >
        {{ 'LOGIN.MFA.DOWNLOAD' | translate }}
      </button>
      <button
        type="button"
        class="btn mfa-secondary-button"
        (click)="copyRecoveryCodes()"
      >
        {{ (codesCopied ? 'LOGIN.MFA.COPIED' : 'LOGIN.MFA.COPY') | translate }}
      </button>
    </div>
    <p class="mfa-check-row">
      <label class="mfa-check" for="mfa-codes-saved">
        <input
          id="mfa-codes-saved"
          name="codesSaved"
          type="checkbox"
          [(ngModel)]="codesSavedConfirmed"
        />
        <span>{{ 'LOGIN.MFA.RECOVERY-CODES-SAVED' | translate }}</span>
      </label>
    </p>
    <div class="mfa-buttons">
      <button
        id="mfa-codes-continue"
        type="button"
        class="btn btn-primary raised login-button"
        [disabled]="!codesSavedConfirmed"
        (click)="codesSaved = true"
      >
        {{ 'LOGIN.MFA.CONTINUE' | translate }}
      </button>
      <button
        type="button"
        class="btn btn-link mfa-link-button"
        (click)="cancel.emit()"
      >
        {{ 'LOGIN.MFA.BACK' | translate }}
      </button>
    </div>
  </ng-container>

  <!-- 2. QR code (no authenticator yet) or code input (authenticator configured) -->
  <ng-template #codeStep>
    <form id="mfaForm" (ngSubmit)="submit()" autocomplete="off">
      <ng-container *ngIf="isSetup; else verifyTitle">
        <h2 class="mfa-title">{{ 'LOGIN.MFA.SETUP-TITLE' | translate }}</h2>
        <p class="mfa-text">{{ 'LOGIN.MFA.SETUP-TEXT' | translate }}</p>
        <img
          id="mfa-qr-code"
          class="mfa-qr-code"
          [src]="challenge.totp.qr_code"
          [alt]="'LOGIN.MFA.QR-ALT' | translate"
        />
        <p class="mfa-text mfa-small">
          {{ 'LOGIN.MFA.CANT-SCAN' | translate }}
          <code class="mfa-secret">{{ challenge.totp.secret_encoded }}</code>
        </p>
        <p class="mfa-text">{{ 'LOGIN.MFA.SETUP-CODE-TEXT' | translate }}</p>
      </ng-container>
      <ng-template #verifyTitle>
        <h2 class="mfa-title">{{ 'LOGIN.MFA.VERIFY-TITLE' | translate }}</h2>
        <p class="mfa-text" *ngIf="!useRecoveryCode">
          {{ 'LOGIN.MFA.VERIFY-TEXT' | translate }}
        </p>
        <p class="mfa-text" *ngIf="useRecoveryCode">
          {{
            'LOGIN.MFA.RECOVERY-TEXT'
              | translate: { count: challenge.recovery_codes_remaining }
          }}
        </p>
      </ng-template>

      <input
        *ngIf="!useRecoveryCode"
        id="mfa-code"
        name="code"
        type="text"
        inputmode="numeric"
        autocomplete="one-time-code"
        maxlength="8"
        class="form-control mfa-input"
        placeholder="123456"
        [(ngModel)]="code"
      />
      <input
        *ngIf="useRecoveryCode"
        id="mfa-recovery-code"
        name="recoveryCode"
        type="text"
        autocomplete="off"
        maxlength="20"
        class="form-control mfa-input mfa-input-wide"
        placeholder="XXXX-XXXX-XXXX"
        [(ngModel)]="recoveryCode"
      />

      <p
        class="mfa-check-row"
        *ngIf="!useRecoveryCode && trustedDeviceDays > 0"
      >
        <label class="mfa-check" for="mfa-remember-device">
          <input
            id="mfa-remember-device"
            name="rememberDevice"
            type="checkbox"
            [(ngModel)]="rememberDevice"
          />
          <span>{{
            'LOGIN.MFA.REMEMBER-DEVICE' | translate: { days: trustedDeviceDays }
          }}</span>
        </label>
      </p>

      <div [ngSwitch]="status" class="mfa-message">
        <span *ngSwitchCase="Status.INVALID_MFA_CODE" class="error">
          {{ 'LOGIN.MFA.INVALID-CODE' | translate }}
        </span>
        <span *ngSwitchCase="Status.INVALID_RECOVERY_CODE" class="error">
          {{ 'LOGIN.MFA.INVALID-RECOVERY-CODE' | translate }}
        </span>
        <span *ngSwitchCase="Status.ERROR" class="error">
          {{ 'LOGIN.ERROR' | translate }}
        </span>
        <span *ngSwitchCase="Status.WAITING" class="success">
          {{ 'LOGIN.WAITING' | translate }}
        </span>
        <span *ngSwitchCase="Status.SUCCESS" class="success">
          {{ 'LOGIN.SUCCESSFUL' | translate }}
        </span>
      </div>

      <div class="mfa-buttons">
        <button
          id="mfa-verify"
          type="submit"
          class="btn btn-primary raised login-button"
          [disabled]="!canSubmit"
        >
          {{ 'LOGIN.MFA.VERIFY' | translate }}
        </button>
        <button
          type="button"
          class="btn btn-link mfa-link-button"
          (click)="cancel.emit()"
        >
          {{ 'LOGIN.MFA.BACK' | translate }}
        </button>
      </div>

      <p class="mfa-text mfa-small" *ngIf="!isSetup">
        <a
          *ngIf="canUseRecoveryCode"
          id="mfa-toggle-recovery"
          href="javascript:void(0)"
          (click)="toggleRecoveryCode()"
        >
          {{
            (useRecoveryCode
              ? 'LOGIN.MFA.USE-AUTHENTICATOR'
              : 'LOGIN.MFA.USE-RECOVERY-CODE'
            ) | translate
          }}
        </a>
        <span *ngIf="!canUseRecoveryCode">
          {{ 'LOGIN.MFA.LOST-ACCESS' | translate }}
          <a [href]="'mailto:' + ('LOGIN.SUPPORT_EMAIL' | translate)">
            {{ 'LOGIN.SUPPORT_EMAIL' | translate }}
          </a>
        </span>
      </p>
    </form>
  </ng-template>
</div>
